import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Users, Lock, ChevronRight, Check, Clock, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * TrendingCommunitiesStrip — compact horizontal-scroll strip of the top
 * communities, shown at the top of the Feed for signed-in users. Each
 * card exposes the Join / Request-to-Join / Requested / Joined state
 * inline so the user never has to leave the Feed to jump into a room.
 *
 * Renders nothing if there are fewer than 3 communities (avoids an
 * empty-looking rail on brand new deployments).
 */
export default function TrendingCommunitiesStrip() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/communities");
      setRows(data);
    } catch {
      setRows([]);
    }
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  if (!user || !rows) return null;
  const trending = rows.slice(0, 6); // top 6 by member count
  if (trending.length < 3) return null;

  const primary = async (c) => {
    if (c.joined) return;
    const wantsRequest = c.visibility === "private" || c.join_policy === "request";
    setBusyId(c.id);
    try {
      if (wantsRequest) {
        await api.post(`/communities/${c.id}/join-requests`, { note: null });
        toast.success(`Request sent — ${c.name}`);
      } else {
        await api.post(`/communities/${c.id}/join`);
        toast.success(`Joined ${c.name}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not act.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mb-5" data-testid="feed-trending-communities">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500">
          Trending communities
        </div>
        <Link
          to="/communities"
          className="text-[11px] text-rose-300 hover:text-rose-200 inline-flex items-center gap-0.5"
          data-testid="feed-trending-communities-see-all"
        >
          See all <ChevronRight size={12} />
        </Link>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-6 px-6 lg:-mx-8 lg:px-8 snap-x snap-mandatory">
        {trending.map((c) => (
          <TrendingCard
            key={c.id}
            community={c}
            busy={busyId === c.id}
            onPrimary={() => primary(c)}
          />
        ))}
      </div>
    </div>
  );
}

function TrendingCard({ community, busy, onPrimary }) {
  const isPrivate = community.visibility === "private";
  const joined = !!community.joined;
  const pending = !!community.has_pending_request;
  const wantsRequest = isPrivate || community.join_policy === "request";

  return (
    <Link
      to={`/c/${community.id}`}
      data-testid={`feed-trending-card-${community.id}`}
      className="shrink-0 w-64 snap-start bg-zinc-950/60 border border-white/10 hover:border-rose-500/40 rounded-md p-3 transition-colors flex flex-col gap-2"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm text-white font-semibold truncate flex items-center gap-1">
            {isPrivate && <Lock size={11} className="text-rose-400 shrink-0" />}
            {community.name}
          </div>
          <div className="text-[11px] text-zinc-500 flex items-center gap-1 mt-0.5">
            <Users size={10} /> {(community.members_count || 0).toLocaleString()}
          </div>
        </div>
        {joined ? (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-emerald-500/10 border border-emerald-400/30 text-emerald-300 rounded-full px-2 py-1">
            <Check size={10} /> Joined
          </span>
        ) : pending ? (
          <span
            data-testid={`feed-trending-requested-${community.id}`}
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-amber-500/10 border border-amber-400/30 text-amber-300 rounded-full px-2 py-1"
          >
            <Clock size={10} /> Requested
          </span>
        ) : (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPrimary(); }}
            disabled={busy}
            data-testid={`feed-trending-primary-${community.id}`}
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-rose-600 hover:bg-rose-500 disabled:opacity-60 text-white rounded-full px-2.5 py-1"
          >
            {busy ? <Loader2 size={10} className="animate-spin" /> : null}
            {wantsRequest ? "Request" : "Join"}
          </button>
        )}
      </div>
      {(!isPrivate || joined) && community.description && (
        <p className="text-[11px] text-zinc-400 leading-relaxed line-clamp-2">
          {community.description}
        </p>
      )}
      {isPrivate && !joined && (
        <p className="text-[11px] text-zinc-500 leading-relaxed italic">
          Members-only — request to unlock.
        </p>
      )}
    </Link>
  );
}
